// Page de revue humaine d'un upload OCR'é.
//
// Layout : split view
//   gauche : PDF en iframe (presigned URL)
//   droite : formulaire pré-rempli avec les 8 champs extraits par l'OCR,
//            + dropdowns pour les liens métier (client/projet/intervenant),
//            + case "Due Upon Receipt",
//            + boutons Valider / Re-OCR / Annuler
//
// Chaque champ texte/date/montant affiche un badge de confiance et un ring
// coloré (vert ≥85, ambre 70-84, rouge <70 ou null).

import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ApiError } from "../api/client";
import { useAuthStore } from "../stores/auth";
import { clientsApi } from "../api/clients";
import { intervenantsApi } from "../api/intervenants";
import { invoiceUploadsApi } from "../api/invoiceUploads";
import { projectsApi } from "../api/projects";
import {
  parseQuotaRefusal,
  quotaCtaLabel,
  type QuotaRefusal,
} from "../api/quotaError";
import type {
  FuzzyMatch,
  InvoiceFromExtractionPayload,
  OcrFields,
  ReviewContext,
  UUID,
} from "../api/types";
import { AppShell } from "../components/AppShell";
import { CurrencySelect } from "../components/CurrencySelect";
import {
  ConfidenceBadge,
  inputRingForConfidence,
} from "../components/ConfidenceBadge";
import { FormField } from "../components/FormField";

type FormState = {
  reference: string;
  issue_date: string;
  due_date: string;
  due_upon_receipt: boolean;
  client_id: UUID | "";
  project_id: UUID | "";
  intervenant_id: UUID | "";
  amount_ht: string;
  amount_ttc: string;
  currency: string;
  invoice_type: "customer" | "supplier";
  notes: string;
};

// Lot 8.4 — un échec de validation peut être soit un message texte simple,
// soit un refus structuré 402 qu'on rend avec sa CTA dédiée vers /pricing.
type SubmitError =
  | { kind: "generic"; message: string }
  | { kind: "quota"; refusal: QuotaRefusal }
| { kind: "duplicate"; invoiceId: string; reference: string };

function emptyForm(): FormState {
  return {
    reference: "",
    issue_date: "",
    due_date: "",
    due_upon_receipt: false,
    client_id: "",
    project_id: "",
    intervenant_id: "",
    amount_ht: "",
    amount_ttc: "",
    currency: "EUR",
    invoice_type: "customer",
    notes: "",
  };
}

/** Convertit les 8 champs OCR + ReviewContext en état initial du formulaire. */
function seedForm(ctx: ReviewContext, tenantCurrency: string): FormState {
  const f = ctx.fields;
  return {
    reference: (f.reference.value ?? "") as string,
    issue_date: (f.issue_date.value ?? "") as string,
    due_date: (f.due_date.value ?? "") as string,
    due_upon_receipt: ctx.suggest_due_upon_receipt,
    client_id: ctx.client_candidates[0]?.id ?? "",
    project_id: ctx.project_candidates[0]?.id ?? "",
    intervenant_id: ctx.intervenant_candidates[0]?.id ?? "",
    amount_ht:
      f.amount_ht.value !== null ? String(f.amount_ht.value) : "",
    amount_ttc:
      f.amount_ttc.value !== null ? String(f.amount_ttc.value) : "",
    currency: tenantCurrency ?? "EUR",
    invoice_type: ctx.upload_invoice_type ?? "customer",
    notes: "",
  };
}

function confOf(fields: OcrFields, key: string): number | null {
  const cell = (fields as Record<string, { value: unknown; confidence: number } | undefined>)[key];
  // Si le champ est absent (ancien OCR sans vendor) ou value null → rouge.
  if (!cell || cell.value === null) return null;
  return cell.confidence;
}

export function UploadReviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [ctx, setCtx] = useState<ReviewContext | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<SubmitError | null>(null);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    let blobUrl: string | null = null;
    void (async () => {
      try {
        const [rc, dl] = await Promise.all([
          invoiceUploadsApi.reviewContext(id),
          invoiceUploadsApi.downloadUrl(id),
        ]);
        if (!alive) return;
        setCtx(rc);
        setForm(seedForm(rc, useAuthStore.getState().currentTenant?.currency ?? "EUR"));
        // dl.url est un chemin /api/... qui nécessite Authorization.
        // On fetch le blob avec le token puis on crée un object URL.
        const { getAccessTokenForUpload } = await import("../api/client");
        const token = getAccessTokenForUpload();
        const proxyPath = dl.url.startsWith("http") ? dl.url : dl.url;
        const resp = await fetch(proxyPath, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!resp.ok) throw new Error(`PDF introuvable (${resp.status})`);
        const blob = await resp.blob();
        blobUrl = URL.createObjectURL(blob);
        if (!alive) { URL.revokeObjectURL(blobUrl); return; }
        setPdfUrl(blobUrl);
      } catch (err) {
        if (!alive) return;
        const msg =
          err instanceof ApiError ? err.detail : "Chargement impossible";
        setLoadError(msg);
      }
    })();
    return () => {
      alive = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [id]);

  const fields = ctx?.fields;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!id) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      if (!form.client_id) {
        throw new Error(form.invoice_type === "supplier" ? "Sélectionne un fournisseur avant de valider" : "Sélectionne un client avant de valider");
      }
      const payload: InvoiceFromExtractionPayload = {
        reference: form.reference.trim(),
        issue_date: form.issue_date,
        due_date: form.due_upon_receipt
          ? null
          : form.due_date || null,
        due_upon_receipt: form.due_upon_receipt,
        client_id: form.client_id,
        project_id: form.project_id || null,
        intervenant_id: form.intervenant_id || null,
        amount_ht: form.amount_ht,
        amount_ttc: form.amount_ttc,
        currency: form.currency || "EUR",
        invoice_type: form.invoice_type,
        notes: form.notes.trim() ? form.notes.trim() : null,
      };
      await invoiceUploadsApi.validate(id, payload);
      navigate("/uploads");
    } catch (err) {
      // Lot 8.4 — un refus 402 est porteur d'une CTA spécifique vers
      // /pricing. On le détecte avant le fallback générique pour ne pas
      // perdre la raison ("trial_expired" vs "invoice_quota_exceeded").
      const refusal = parseQuotaRefusal(err);
      if (refusal) {
        setSubmitError({ kind: "quota", refusal });
      } else if (err instanceof ApiError && err.status === 409 && typeof err.detail === "string" && err.detail.startsWith("DUPLICATE_REFERENCE:")) {
        const parts = err.detail.split(":");
        const invoiceId = parts[1] ?? "";
        setSubmitError({ kind: "duplicate", invoiceId, reference: form.reference });
      } else {
        const msg =
          err instanceof ApiError
            ? err.detail
            : err instanceof Error
              ? err.message
              : "Validation impossible";
        setSubmitError({ kind: "generic", message: msg });
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function onReOcr() {
    if (!id) return;
    if (!window.confirm("Relancer l'OCR sur ce PDF ? L'extraction actuelle sera remplacée.")) {
      return;
    }
    try {
      await invoiceUploadsApi.reOcr(id);
      navigate("/uploads");
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Relance impossible";
      window.alert(msg);
    }
  }

  const avgBucket = useMemo(() => {
    if (!ctx) return null;
    if (ctx.average_confidence >= 85) return "green";
    if (ctx.average_confidence >= 70) return "amber";
    return "red";
  }, [ctx]);

  if (loadError) {
    return (
      <AppShell>
        <p role="alert" className="text-red-600">{loadError}</p>
      </AppShell>
    );
  }
  if (!ctx || !fields) {
    return (
      <AppShell>
        <p className="text-slate-500">Chargement de l'extraction…</p>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">
          Revue de la facture
        </h1>
        <p className="text-sm text-slate-500">
          Template détecté : <span className="font-medium">{ctx.detected_template}</span>
          {" · "}
          Confiance moyenne :{" "}
          <span className={`font-medium ${
            avgBucket === "green" ? "text-emerald-700"
            : avgBucket === "amber" ? "text-amber-700"
            : "text-red-700"
          }`}>
            {ctx.average_confidence}%
          </span>
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Panneau gauche : PDF */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden min-h-[600px]">
          {pdfUrl ? (
            <iframe
              src={pdfUrl}
              title="Aperçu de la facture"
              className="w-full h-[80vh]"
            />
          ) : (
            <p className="p-6 text-sm text-slate-500">Chargement du PDF…</p>
          )}
        </div>

        {/* Panneau droit : formulaire */}
        <form
          onSubmit={onSubmit}
          aria-label="Formulaire de revue"
          className="bg-white rounded-xl border border-slate-200 p-6 space-y-4"
        >
          <div>
            <div className="flex items-center justify-between mb-1">
              <label
                htmlFor="reference"
                className="block text-sm font-medium text-slate-700"
              >
                Référence *
              </label>
              <ConfidenceBadge value={confOf(fields, "reference")} compact />
            </div>
            <input
              id="reference"
              required
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              className={`input ${inputRingForConfidence(confOf(fields, "reference"))}`}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="issue_date" className="block text-sm font-medium text-slate-700">
                  Date d'émission *
                </label>
                <ConfidenceBadge value={confOf(fields, "issue_date")} compact />
              </div>
              <input
                id="issue_date"
                type="date"
                required
                value={form.issue_date}
                onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
                className={`input ${inputRingForConfidence(confOf(fields, "issue_date"))}`}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="due_date" className="block text-sm font-medium text-slate-700">
                  Date d'échéance
                </label>
                <ConfidenceBadge value={confOf(fields, "due_date")} compact />
              </div>
              <input
                id="due_date"
                type="date"
                value={form.due_date}
                disabled={form.due_upon_receipt}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className={`input ${inputRingForConfidence(confOf(fields, "due_date"))} ${
                  form.due_upon_receipt ? "bg-slate-50 text-slate-400" : ""
                }`}
              />
              <label className="mt-1 flex items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={form.due_upon_receipt}
                  onChange={(e) =>
                    setForm({ ...form, due_upon_receipt: e.target.checked })
                  }
                />
                Due Upon Receipt (échéance = émission)
              </label>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Type de facture</span>
            <select
              value={form.invoice_type}
              onChange={e => setForm({ ...form, invoice_type: e.target.value as "customer" | "supplier" })}
              className="text-sm rounded-md border border-slate-200 px-2 py-1"
            >
              <option value="customer">📥 Client (revenu)</option>
              <option value="supplier">📤 Fournisseur (dépense)</option>
            </select>
          </div>

          <FuzzyPicker
            label={form.invoice_type === "supplier" ? "Fournisseur *" : "Client *"}
            htmlFor="client_id"
            value={form.client_id}
            candidates={ctx.client_candidates}
            ocrGuess={(form.invoice_type === "supplier" ? fields.vendor?.value : fields.client?.value) as string | null}
            ocrConfidence={form.invoice_type === "supplier" ? confOf(fields, "vendor") : confOf(fields, "client")}
            onChange={(v) => setForm({ ...form, client_id: v })}
            onAutoCreate={async (name) => {
              const c = await clientsApi.create({ name });
              // Injecter le nouveau client dans la liste des candidats
              ctx.client_candidates.push({ id: c.id, name: c.name, confidence: 1, auto_created: true });
              return c.id;
            }}
          />

          <FuzzyPicker
            label="Projet"
            htmlFor="project_id"
            value={form.project_id}
            candidates={ctx.project_candidates}
            ocrGuess={fields.project.value as string | null}
            ocrConfidence={confOf(fields, "project")}
            onChange={(v) => setForm({ ...form, project_id: v })}
            optional
            allowManual
            blockedReason={!form.client_id ? "Sélectionne d'abord un client pour créer un projet" : null}
            onAutoCreate={async (name) => {
              if (!form.client_id) return null;
              const p = await projectsApi.create({ name, client_id: form.client_id });
              ctx.project_candidates.push({ id: p.id, name: p.name, confidence: 1, auto_created: true });
              return p.id;
            }}
          />

          <FuzzyPicker
            label="Intervenant"
            htmlFor="intervenant_id"
            value={form.intervenant_id}
            candidates={ctx.intervenant_candidates}
            ocrGuess={fields.contributor.value as string | null}
            ocrConfidence={confOf(fields, "contributor")}
            onChange={(v) => setForm({ ...form, intervenant_id: v })}
            optional
            allowManual
            onAutoCreate={async (name) => {
              const i = await intervenantsApi.create({ name });
              ctx.intervenant_candidates.push({ id: i.id, name: i.name, confidence: 1, auto_created: true });
              return i.id;
            }}
          />

          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="amount_ht" className="block text-sm font-medium text-slate-700">
                  Montant HT *
                </label>
                <ConfidenceBadge value={confOf(fields, "amount_ht")} compact />
              </div>
              <input
                id="amount_ht"
                type="number"
                step="0.01"
                required
                value={form.amount_ht}
                onChange={(e) => setForm({ ...form, amount_ht: e.target.value })}
                className={`input ${inputRingForConfidence(confOf(fields, "amount_ht"))}`}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="amount_ttc" className="block text-sm font-medium text-slate-700">
                  Montant TTC *
                </label>
                <ConfidenceBadge value={confOf(fields, "amount_ttc")} compact />
              </div>
              <input
                id="amount_ttc"
                type="number"
                step="0.01"
                required
                value={form.amount_ttc}
                onChange={(e) => setForm({ ...form, amount_ttc: e.target.value })}
                className={`input ${inputRingForConfidence(confOf(fields, "amount_ttc"))}`}
              />
            </div>
            <FormField label="Devise" htmlFor="currency">
              <CurrencySelect
                id="currency"
                value={form.currency}
                onChange={v => setForm({ ...form, currency: v })}
              />
            </FormField>
          </div>

          <FormField label="Notes" htmlFor="notes">
            <textarea
              id="notes"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="input"
            />
          </FormField>

          {submitError?.kind === "generic" && (
            <p role="alert" className="text-sm text-red-600">
              {submitError.message}
            </p>
          )}
          {submitError?.kind === "quota" && (
            <div
              role="alert"
              data-testid="quota-refusal"
              data-reason={submitError.refusal.reason}
              className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            >
              <span>{submitError.refusal.message}</span>
              <Link
                to="/pricing"
                className="rounded-md bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700 self-start sm:self-auto"
              >
                {quotaCtaLabel(submitError.refusal.reason)}
              </Link>
            </div>
          )}

          {submitError?.kind === "duplicate" && (
            <div
              role="alert"
              className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            >
              <span>
                Une facture avec la référence <strong>{submitError.reference}</strong> existe déjà.
              </span>
              <Link
                to={`/invoices/${submitError.invoiceId}`}
                className="rounded-md bg-amber-600 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-700 self-start sm:self-auto"
              >
                Voir la facture existante
              </Link>
            </div>
          )}
          <div className="flex justify-between pt-2">
            <button
              type="button"
              className="text-sm text-slate-500 hover:text-slate-800"
              onClick={onReOcr}
            >
              Relancer l'OCR
            </button>
            <div className="flex gap-3">
              <button
                type="button"
                className="text-sm text-slate-500 hover:text-slate-800"
                onClick={() => navigate("/uploads")}
              >
                Annuler
              </button>
              <button type="submit" disabled={submitting} className="btn-primary">
                {submitting ? "Validation…" : "Valider et créer la facture"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// FuzzyPicker — dropdown pour sélectionner une entité suggérée
// ---------------------------------------------------------------------------

type FuzzyPickerProps = {
  label: string;
  htmlFor: string;
  value: UUID | "";
  candidates: FuzzyMatch[];
  ocrGuess: string | null;
  ocrConfidence: number | null;
  onChange: (v: UUID | "") => void;
  onAutoCreate?: (name: string) => Promise<UUID | null>;
  optional?: boolean;
  /** Affiche un champ texte manuel en plus du bouton OCR */
  allowManual?: boolean;
  /** Message à afficher si l'auto-création est bloquée (ex: client non sélectionné) */
  blockedReason?: string | null;
};

function FuzzyPicker({
  label,
  htmlFor,
  value,
  candidates,
  ocrGuess,
  ocrConfidence,
  onChange,
  onAutoCreate,
  optional = false,
  allowManual = false,
  blockedReason = null,
}: FuzzyPickerProps) {
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [manualName, setManualName] = useState("");
  const [showManual, setShowManual] = useState(false);

  async function handleCreate(name: string) {
    if (!onAutoCreate || !name.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      const id = await onAutoCreate(name.trim());
      if (id) {
        onChange(id);
        setShowManual(false);
        setManualName("");
      }
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : "Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  }

  const isEmpty = candidates.length === 0;
  const canCreate = !!onAutoCreate && !blockedReason;

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label htmlFor={htmlFor} className="block text-sm font-medium text-slate-700">
          {label}
        </label>
        <ConfidenceBadge value={ocrConfidence} compact />
      </div>
      {ocrGuess !== null && (
        <p className="text-xs text-slate-400 mb-1">
          OCR a lu : <span className="italic">« {ocrGuess} »</span>
        </p>
      )}

      {/* Dropdown si des candidats existent */}
      {!isEmpty && (
        <>
          <select
            id={htmlFor}
            required={!optional}
            value={value}
            onChange={(e) => onChange(e.target.value as UUID | "")}
            className={`input ${inputRingForConfidence(ocrConfidence)}`}
          >
            {optional && <option value="">— Aucun —</option>}
            {!optional && !value && <option value="">— Sélectionner —</option>}
            {candidates.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}{(c.confidence < 1 && !c.auto_created) ? ` (${Math.round(c.confidence * 100)}%)` : ""}{c.auto_created ? " ✨ nouveau" : ""}
              </option>
            ))}
          </select>
          {candidates.some((c) => c.auto_created) && (
            <p className="text-xs text-emerald-600 mt-1">
              ✨ Entité créée automatiquement à partir de l'OCR
            </p>
          )}
        </>
      )}

      {/* Message aucun match */}
      {isEmpty && (
        <p className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1 border border-amber-200 mb-2">
          Aucun match proposé{optional ? " (champ optionnel)." : "."}
        </p>
      )}

      {/* Raison de blocage (ex: client non sélectionné) */}
      {blockedReason && (
        <p className="text-xs text-slate-500 italic mb-1">{blockedReason}</p>
      )}

      {/* Boutons de création */}
      {canCreate && (
        <div className="flex flex-wrap gap-1 mt-1">
          {/* Bouton OCR si une valeur a été lue */}
          {ocrGuess && !showManual && (
            <button
              type="button"
              onClick={() => handleCreate(ocrGuess)}
              disabled={creating}
              className="text-xs px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition"
            >
              {creating ? "Création…" : `✨ Créer « ${ocrGuess} »`}
            </button>
          )}
          {/* Bouton saisie manuelle */}
          {(allowManual || !ocrGuess) && !showManual && (
            <button
              type="button"
              onClick={() => setShowManual(true)}
              disabled={creating}
              className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50 transition border border-slate-300"
            >
              ✏️ Saisir un nom
            </button>
          )}
        </div>
      )}

      {/* Champ de saisie manuelle */}
      {canCreate && showManual && (
        <div className="flex gap-1 mt-1 items-center">
          <input
            type="text"
            value={manualName}
            onChange={(e) => setManualName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleCreate(manualName); } }}
            placeholder={`Nom du ${label.toLowerCase()}…`}
            className="input text-sm flex-1"
            autoFocus
          />
          <button
            type="button"
            onClick={() => handleCreate(manualName)}
            disabled={creating || !manualName.trim()}
            className="text-xs px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition"
          >
            {creating ? "…" : "Créer"}
          </button>
          <button
            type="button"
            onClick={() => { setShowManual(false); setManualName(""); setCreateError(null); }}
            className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-600 hover:bg-slate-200 transition border border-slate-300"
          >
            Annuler
          </button>
        </div>
      )}

      {createError && (
        <p className="text-xs text-red-600 mt-1">{createError}</p>
      )}
    </div>
  );
}
